import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material/dialog';

import * as QRCode from 'qrcode';

import {
  MessstelleQrInfo
} from '../../../../Models/messstelle-qr.model';

import { QrDataService } from '../../../../Services/offline/qr-data.service';


export interface MessstelleQrDialogData {
  messstelleId: number;

  kennzeichnungLabel?: string | null;
  kennzeichnung?: string | null;
  langtext?: string | null;

  projektName?: string | null;
  projektNummer?: string | null;
}


@Component({
  selector: 'app-messstelle-qr-dialog',
  templateUrl: './messstelle-qr-dialog.component.html',
  styleUrls: ['./messstelle-qr-dialog.component.css']
})
export class MessstelleQrDialogComponent implements OnInit {

  info: MessstelleQrInfo | null = null;

  qrDataUrl = '';

  isLoading = true;

  loadError = '';

  copying = false;


  /** True, wenn der Code aus dem lokalen Bestand stammt (ohne Verbindung). */
  offline = false;

  /** True, wenn die Zuordnung offline erfasst wurde und noch nicht am Server ist. */
  wartend = false;


  constructor(
    private service: QrDataService,
    private dialogRef: MatDialogRef<MessstelleQrDialogComponent>,

    @Inject(MAT_DIALOG_DATA)
    public data: MessstelleQrDialogData
  ) { }


  ngOnInit(): void {

    this.ladeOderErstelle();
  }


  // ========================================================
  // ANZEIGEDATEN
  // ========================================================

  get kennzeichnungTitel(): string {

    const label =
      this.data.kennzeichnungLabel?.trim()
      || 'Kennzeichnung';

    const wert =
      this.data.kennzeichnung?.trim()
      || '–';

    return `${label}: ${wert}`;
  }


  get projektText(): string {

    const nummer =
      this.data.projektNummer?.trim()
      || '';

    const name =
      this.data.projektName?.trim()
      || '';

    if (nummer && name) {
      return `${nummer} · ${name}`;
    }

    return nummer || name || '–';
  }


  // ========================================================
  // QR LADEN / ERSTELLEN
  // ========================================================

  private ladeOderErstelle(): void {

    this.isLoading = true;

    this.loadError = '';

    this.service
      .getAktuellerQr(this.data.messstelleId)
      .subscribe({

        next: ergebnis => {

          this.info = ergebnis.daten;

          // Ohne Verbindung kommt der Code aus dem lokalen Bestand; eine
          // offline erfasste Zuordnung trägt noch keine Server-Id.
          this.offline = ergebnis.quelle === 'offline';
          this.wartend = this.offline && ergebnis.daten.id === 0;

          this.baueQr(ergebnis.daten.publicUrl);
        },

        error: err => {

          console.error(
            'QR-Code konnte nicht geladen werden:',
            err
          );

          this.isLoading = false;

          /*
           * 404 heißt: der Messstelle ist noch kein QR-Code aus dem
           * Pool zugeordnet. Das ist kein Fehler, sondern ein Hinweis.
           */
          this.loadError =
            err?.status === 404 || /offline kein QR-Code/.test(err?.message ?? '')
              ? 'Dieser Messstelle ist noch kein QR-Code zugeordnet. '
                + 'Bitte zuerst über „QR-Code vergeben“ einen '
                + 'Sticker scannen.'
              : err?.error?.message
                ?? err?.message
                ?? 'Der QR-Code konnte nicht geladen werden.';
        }
      });
  }


  private async baueQr(
    publicUrl: string
  ): Promise<void> {

    try {

      this.qrDataUrl =
        await QRCode.toDataURL(
          publicUrl,
          {
            width: 640,
            margin: 2,
            errorCorrectionLevel: 'M'
          }
        );

      this.isLoading = false;
    }
    catch (err) {

      console.error(
        'QR-Bild konnte nicht erzeugt werden:',
        err
      );

      this.isLoading = false;

      this.loadError =
        'Der QR-Code konnte nicht dargestellt werden.';
    }
  }


  // ========================================================
  // PNG
  // ========================================================

  downloadPng(): void {

    if (
      !this.qrDataUrl
      ||
      !this.info
    ) {
      return;
    }

    const a =
      document.createElement('a');

    a.href =
      this.qrDataUrl;

    a.download =
      `messstelle-${this.data.messstelleId}-qr.png`;

    document.body.appendChild(a);

    a.click();

    a.remove();
  }


  // ========================================================
  // DRUCKEN
  // ========================================================

  drucken(): void {

    if (
      !this.qrDataUrl
      ||
      !this.info
    ) {
      return;
    }

    const fenster =
      window.open(
        '',
        '_blank',
        'width=720,height=850'
      );

    if (!fenster) {
      return;
    }

    const kennzeichnungLabel =
      this.escapeHtml(
        this.data.kennzeichnungLabel?.trim()
        || 'Kennzeichnung'
      );

    const kennzeichnung =
      this.escapeHtml(
        this.data.kennzeichnung?.trim()
        || '–'
      );

    const langtext =
      this.escapeHtml(
        this.data.langtext?.trim()
        || '–'
      );

    const projekt =
      this.escapeHtml(
        this.projektText
      );


    fenster.document.write(`
      <!doctype html>
      <html lang="de">

        <head>

          <meta charset="utf-8">

          <title>
            QR-Code Messstelle
          </title>


          <style>

            @page {
              size: A4;
              margin: 18mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              font-family: Arial, sans-serif;
              color: #111;
            }

            .label {
              width: 90mm;
              min-height: 118mm;

              border: 1px solid #bbb;
              border-radius: 4mm;

              padding: 7mm;

              display: flex;
              flex-direction: column;

              align-items: center;

              text-align: center;
            }

            .kennzeichnung-label {
              margin-top: 1mm;

              color: #666;

              font-size: 8.5pt;
              font-weight: 700;

              text-transform: uppercase;
              letter-spacing: .4px;
            }

            h1 {
              margin: 1.5mm 0 0;

              max-width: 74mm;

              font-size: 19pt;
              line-height: 1.1;

              overflow-wrap: anywhere;
            }

            .langtext {
              margin-top: 3mm;

              max-width: 74mm;

              color: #333;

              font-size: 10.5pt;
              font-weight: 600;
              line-height: 1.25;

              overflow-wrap: anywhere;
            }

            .projekt {
              margin-top: 2.5mm;
              margin-bottom: 5mm;

              max-width: 74mm;

              color: #666;

              font-size: 9pt;
              line-height: 1.25;

              overflow-wrap: anywhere;
            }

            img {
              width: 56mm;
              height: 56mm;

              image-rendering: pixelated;
            }

            .hinweis {
              margin-top: 5mm;

              font-size: 9pt;
              font-weight: 700;
            }

            .url {
              margin-top: 3mm;

              max-width: 72mm;

              overflow-wrap: anywhere;

              font-size: 7pt;

              color: #777;
            }

            @media print {

              body {
                padding: 0;
              }

              .label {
                border-color: #aaa;
              }
            }

          </style>

        </head>


        <body>

          <div class="label">

            <div class="kennzeichnung-label">
              ${kennzeichnungLabel}
            </div>

            <h1>
              ${kennzeichnung}
            </h1>

            <div class="langtext">
              ${langtext}
            </div>

            <div class="projekt">
              Projekt: ${projekt}
            </div>

            <img
              src="${this.qrDataUrl}"
              alt="QR-Code">

            <div class="hinweis">
              Scannen für Nur-Lese-Zugriff
            </div>

            <div class="url">
              ${this.escapeHtml(this.info.publicUrl)}
            </div>

          </div>


          <script>

            window.onload = function () {
              window.print();
            };

          </script>

        </body>

      </html>
    `);

    fenster.document.close();
  }


  // ========================================================
  // LINK
  // ========================================================

  async linkKopieren(): Promise<void> {

    if (!this.info) {
      return;
    }

    try {

      this.copying = true;

      await navigator.clipboard.writeText(
        this.info.publicUrl
      );
    }
    finally {

      this.copying = false;
    }
  }


  schliessen(): void {

    this.dialogRef.close();
  }


  private escapeHtml(
    wert: string
  ): string {

    return wert
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}
